import React, { useState, useReducer, useEffect, useContext } from "react";
import {
  CampaignReducer,
  SET_ITEM,
  NEW_BUNDLE,
  DELETE_BUNDLE,
  DELETE_GOAL,
  DELETE_OCU_PRODUCT,
  NEW_GOAL,
  DISCARD,
  NEW_OCU_PRODUCT,
  UPDATE_OLD_OCU
} from "reducers/CampaignReducer";
import { useMutation } from "@apollo/react-hooks";
import useReactRouter from "use-react-router";
import { Loading, Toast } from "@shopify/app-bridge-react";
import {
  CREATE_CAMPAIGN_MUTATION,
  UPDATE_CAMPAIGN_MUTATION
} from "lib/mutations";
import { LIST_SHOP_CAMPAIGNS, GET_SINGLE_CAMPAIGN } from "Queries";
import { SHOPIFY_API_KEY, SHOPIFY_EMBEDDED_FORCE_REDIRECT } from "aws-exports";
import { NEW_CAMPAIGN_INITIAL_STATE } from "lib/globals";
import { Run } from "lib/validate";
import { CampaignConstraints } from "Constraints";
import removeTypeName, { removeByType } from "lib/removeTypeName";
import UserContext from "context/UserContext";
import ShopContext from "context/ShopContext";
import _ from "lodash";

const CampaignContext = React.createContext(null);
export const CampaignProvider = CampaignContext.Provider;

export const withCampaignProvider = (
  ChildComponent,
  campaign = null,
  duplication = false
) => {
  return () => {
    const user = useContext(UserContext);
    const {
      state: {
        settings: {
          theme: { primary, secondary, background },
          font,
          modules: { checkout_button, order_note },
          default_language,
          multiCurrencyMode,
          product_details
        }
      }
    } = useContext(ShopContext);

    if (!campaign)
      campaign = NEW_CAMPAIGN_INITIAL_STATE(
        {
          checkout_button,
          primary,
          secondary,
          font_color: font.color,
          background,
          multiCurrencyMode
        },
        default_language
      );
    else {
      if (!campaign.settings.modules.order_note) {
        campaign.settings.modules.order_note = order_note;
        campaign.settings.modules.order = [
          ...campaign.settings.modules.order,
          "order_note"
        ];
      }
      if (!campaign.target.countries) {
        campaign.target.countries =
          NEW_CAMPAIGN_INITIAL_STATE().target.countries;
      }

      // migrations
      if (!campaign.settings.add_to_unlock.product_details) {
        campaign.settings.add_to_unlock.product_details = product_details;
      }

      if (!campaign.settings.one_check_up_sell?.highlight && !campaign.settings.multi_one_check_upsell) {
        campaign.settings.one_check_up_sell.highlight = primary;
      }
      if (!campaign.settings.add_to_unlock.progress_primary) {
        campaign.settings.add_to_unlock.progress_primary = primary;
        campaign.settings.add_to_unlock.progress_secondary = secondary;
        campaign.settings.add_to_unlock.progress_complete = primary;
      }
      if (!campaign.settings.add_to_unlock.upsell_add_button.color) {
        campaign.settings.add_to_unlock.upsell_add_button.color = primary;
      }
    }
    let originalCampaign = { ...campaign };
    if (duplication) {
      originalCampaign.name = originalCampaign.name + " ";
    }
    const [validationErrors, setValidationErrors] = useState({});
    const [originalState, setOriginalState] = useState(originalCampaign);
    const [showValidationError, setShowValidationError] = useState("");
    const [state, dispatch] = useReducer(
      CampaignReducer(default_language),
      campaign
    );
    const { history } = useReactRouter();
    let [
      CreateCampaignMutation,
      {
        loading: CreateCampaignMutation_Loading,
        error: CreateCampaignMutation_Error,
        data: CreateCampaignMutation_Data
      }
    ] = useMutation(CREATE_CAMPAIGN_MUTATION);

    let [
      UpdateCampaignMutation,
      {
        loading: UpdateCampaignMutation_Loading,
        error: UpdateCampaignMutation_Error,
        data: UpdateCampaignMutation_Data
      }
    ] = useMutation(UPDATE_CAMPAIGN_MUTATION);

    const setItem = (path, value) => dispatch({ type: SET_ITEM, path, value });
    const newBundle = () => dispatch({ type: NEW_BUNDLE });
    const deleteBundle = (value) => dispatch({ type: DELETE_BUNDLE, value });

    const newGoal = () => dispatch({ type: NEW_GOAL });
    const deleteGoal = (value) => dispatch({ type: DELETE_GOAL, value });
    const newOneCheckUpsellProduct = () => dispatch({ type: NEW_OCU_PRODUCT });
    const deleteOneCheckUpsellProduct = (value) => dispatch({ type: DELETE_OCU_PRODUCT, value });
    const updateOldOCU = () => {
      const { highlight, ...rest } = campaign.settings.one_check_up_sell
      const value = {
        variant: "switch",
        align: "natural",
        border: false,
        border_color: { r: 226, g: 232, b: 240, a: 1 },
        switch_on: primary,
        switch_off: secondary,
        highlight,
        products: [rest]
      }
      dispatch({ type: UPDATE_OLD_OCU, value });
    }

    const discard = () => {
      dispatch({ type: DISCARD, value: originalState });
    };
    const validate = (state, specific = [], silent = false) => {
      return Run(
        state,
        specific,
        {
          errors: validationErrors,
          setErrors: setValidationErrors,
          constraints: CampaignConstraints
        },
        silent
      );
    };

    const saveCampaign = async (overrides = {}) => {
      const errors = validate(state);
      console.log("errors", errors);
      if (errors) {
        const flatErrors = Object.values(errors)
          .map((error) => error.filter(Boolean))
          .flat(Infinity);
        console.log("flatErrors", flatErrors);
        const errorMessage =
          typeof flatErrors[0] === "object"
            ? Object.values(flatErrors[0]).flat(Infinity)[0]
            : flatErrors[0];
        console.log("errorMessage", errorMessage);
        if (errorMessage) setShowValidationError(errorMessage);
        if (flatErrors.length && errorMessage) return errors;
      }
      if (state.settings.type === "add_to_unlock") {
        // making sure one of [progress,recommendations] is enabled
        const { add_to_unlock } = state.settings;
        if (!add_to_unlock.isActive && !add_to_unlock.show_suggestions) {
          setShowValidationError(
            "Please enable add to unlock or products to suggest."
          );
          const newErrors = {
            "settings.add_to_unlock.isActive": [
              "Please enable add to unlock or products to suggest."
            ],
            "settings.add_to_unlock.show_suggestions": [
              "Please enable add to unlock or products to suggest."
            ]
          };
          setValidationErrors(newErrors);
          return newErrors;
        }

        const goals = add_to_unlock.goals.map(({ goal }) => goal);
        const goalsErrors = {
          "settings.add_to_unlock.goals": []
        };
        goals.forEach((goal, index) => {
          const nextGoal = goals[index + 1];
          if (nextGoal && nextGoal <= goal) {
            goalsErrors["settings.add_to_unlock.goals"].push({
              goal: `Spend goal must be greater than ${goal} on "Reward ${index + 2
                }"`
            });
          } else {
            goalsErrors["settings.add_to_unlock.goals"].push(-index);
          }
        });
        const truthyErrors = goalsErrors["settings.add_to_unlock.goals"].filter(
          (e) => typeof e === "object"
        );
        if (truthyErrors.length) {
          setShowValidationError(truthyErrors[0].goal);
          setValidationErrors(goalsErrors);
          return goalsErrors;
        }
      }
      state.target.ids = state.target.selected.map(({ id }) => id);
      //remove empty strings because dynamodb doesn't allow it
      //and null values because there may be some new settings
      // const saveState = _.merge(
      // 	{},
      // 	NEW_CAMPAIGN_INITIAL_STATE(),
      // 	removeByType(removeTypeName(state), (v) => v !== '' && v !== null)
      // );

      // const saveState = _.mergeWith(
      // 	NEW_CAMPAIGN_INITIAL_STATE(),
      // 	removeByType(removeTypeName(state), (v) => v !== '' && v !== null),
      // 	(objValue, srcValue) => srcValue && _.isArray(srcValue) ? srcValue : undefined
      // );

      const saveState = removeByType(
        removeTypeName(state),
        (v) => v !== "" && v !== null
      );
      // console.log("saveState", saveState);
      console.log("user", user);
      if (!state.id) {
        const {
          data: { createCampaign }
        } = await CreateCampaignMutation({
          variables: {
            input: {
              ...saveState,
              ...{ ...overrides, active: true }
            }
          },
          refetchQueries: [
            {
              query: LIST_SHOP_CAMPAIGNS,
              variables: {
                userId: user.attributes.sub
              }
            }
          ]
        });
        history.push("/campaigns/" + createCampaign.id);
      } else {
        const { id, ...input } = { ...saveState, ...overrides };
        await UpdateCampaignMutation({
          variables: { id, input },
          refetchQueries: [
            {
              query: LIST_SHOP_CAMPAIGNS,
              variables: {
                userId: user.attributes.sub
              }
            },
            {
              query: GET_SINGLE_CAMPAIGN,
              variables: {
                id
              }
            }
          ]
        });
      }
      setOriginalState(state);
    };

    return (
      <CampaignProvider
        value={{
          originalState,
          discard,
          state,
          setItem,
          newBundle,
          newGoal,
          newOneCheckUpsellProduct,
          deleteGoal,
          deleteBundle,
          deleteOneCheckUpsellProduct,
          dispatch,
          saveCampaign,
          validate,
          validationErrors,
          updateOldOCU
        }}
      >
        {(CreateCampaignMutation_Loading || UpdateCampaignMutation_Loading) && (
          <Loading />
        )}
        {(CreateCampaignMutation_Error || UpdateCampaignMutation_Error) && (
          <Toast
            content={`Something went wrong`}
            duration={3000}
            error
            onDismiss={() => {
              UpdateCampaignMutation_Error = null;
              CreateCampaignMutation_Error = null;
            }}
          />
        )}
        {(CreateCampaignMutation_Data || UpdateCampaignMutation_Data) && (
          <Toast
            content={`Campaign has been ${state.id ? "updated" : "saved"}`}
            duration={3000}
            onDismiss={() => { }}
          />
        )}
        {showValidationError && (
          <Toast
            content={showValidationError}
            error={true}
            duration={3000}
            onDismiss={() => setShowValidationError("")}
          />
        )}
        <ChildComponent />
      </CampaignProvider>
    );
  };
};
export default CampaignContext;
